import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { getAnalysis } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Info,
  ShieldAlert,
  Loader2,
  Zap,
  Wallet,
} from "lucide-react";
import ReportSummary from "@/components/ReportSummary";
import DocumentsSection from "@/components/DocumentsSection";
import UpsellSection from "@/components/UpsellSection";
import PaywallSection from "@/components/PaywallSection";
import ProvidersSection from "@/components/ProvidersSection";
import ComparisonSection from "@/components/ComparisonSection";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import BundleUpsell from "@/components/BundleUpsell";
import SmartOverstapBanner from "@/components/SmartOverstapBanner";
import LoadingState from "@/components/LoadingState";

export default function Report() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const justUnlocked = params.get("unlocked") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollTimer = useRef(null);

  const refresh = async () => {
    try {
      const result = await getAnalysis(id);
      setData(result);
      return result;
    } catch (err) {
      toast.error("Analyse kon niet geladen worden.");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Polling while state='processing'
  useEffect(() => {
    if (!data) return;
    if (data.state === "processing") {
      pollTimer.current = setTimeout(async () => {
        await refresh();
      }, 2500);
      return () => clearTimeout(pollTimer.current);
    }
    // Clear poll when ready/failed
    if (pollTimer.current) clearTimeout(pollTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (justUnlocked && data && data.state === "ready") {
      toast.success("Rapport ontgrendeld! Geniet van je volledige analyse.");
    }
  }, [justUnlocked, data]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="report-loading">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Rapport laden...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="report-not-found">
        <div className="text-center max-w-md">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Analyse niet gevonden</h2>
          <p className="mt-2 text-slate-600">Mogelijk is de link verlopen of niet correct.</p>
          <Link
            to="/"
            className="inline-block mt-6 bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg px-6 py-3"
            data-testid="back-home-btn"
          >
            Terug naar home
          </Link>
        </div>
      </div>
    );
  }

  // Still processing — show loading state
  if (data.state === "processing") {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="report-processing">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center">
                <Zap className="h-5 w-5" />
              </div>
              <span className="font-heading font-bold text-lg text-slate-900">EnergieKontract</span>
            </Link>
          </div>
        </header>
        <LoadingState />
      </div>
    );
  }

  // Failed
  if (data.state === "failed") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="report-failed">
        <div className="text-center max-w-md bg-white border border-slate-200 rounded-2xl p-10">
          <div className="mx-auto h-14 w-14 rounded-full bg-red-100 grid place-items-center mb-4">
            <AlertTriangle className="h-7 w-7 text-red-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Analyse mislukt</h2>
          <p className="mt-2 text-slate-600 text-sm">
            {data.error || "Er ging iets mis tijdens het analyseren. Probeer het opnieuw met een ander bestand."}
          </p>
          <Link
            to="/"
            className="inline-block mt-6 bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg px-6 py-3"
            data-testid="report-failed-retry-btn"
          >
            Probeer opnieuw
          </Link>
        </div>
      </div>
    );
  }

  const { analysis, input, teaser, report_unlocked, documents_unlocked } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="report-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-white/85">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="report-logo-link">
            <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center">
              <Zap className="h-5 w-5" />
            </div>
            <span className="font-heading font-bold text-lg text-slate-900">EnergieKontract</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700 transition-colors"
            data-testid="report-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Nieuwe check
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        {/* Title + Risk */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 fade-up">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              {report_unlocked ? "Analyse rapport" : "Voorvertoning"}
            </span>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              {report_unlocked ? "Je contract analyse is klaar" : "Je analyse is gereed"}
            </h1>
            <p className="mt-2 text-slate-600">
              Op basis van {input?.contract_filename || "je geüploade contract"} · termijnbedrag €{input?.monthly_amount}
            </p>
          </div>
          <RiskBadge score={(analysis?.risk_score) || teaser?.risk_score} />
        </div>

        {/* ============ TEASER MODE (NOT UNLOCKED) ============ */}
        {!report_unlocked && (
          <>
            {/* Voorschot warning teaser — als AI gevonden heeft dat klant naheffing krijgt, laten zien VOOR de paywall */}
            {teaser?.voorschot_warning?.is_under_paying && teaser.voorschot_warning.yearly_difference > 0 && (
              <VoorschotTeaser
                yearlyDifference={teaser.voorschot_warning.yearly_difference}
                severity={teaser.voorschot_warning.severity}
              />
            )}
            <PaywallSection analysisId={id} teaser={teaser} />
            {/* Comparison ook in teaser mode — laat gebruikers direct overstappen
                zonder te hoeven betalen. Verdient affiliate commissie. */}
            <ComparisonSection analysisId={id} possibleSavings={teaser?.possible_savings_estimate || 0} />
            {/* Exit-intent popup vangt vertrekkende bezoekers — leidt naar /vergelijk
                voor extra affiliate conversies. Toont alleen in teaser-modus. */}
            <ExitIntentPopup
              enabled={!report_unlocked}
              naheffingAmount={teaser?.voorschot_warning?.yearly_difference || 0}
              savingsEstimate={teaser?.possible_savings_max || teaser?.possible_savings_estimate || 0}
            />
          </>
        )}

        {/* ============ UNLOCKED MODE ============ */}
        {report_unlocked && analysis && (
          <>
            {/* Smart overstap CTA — direct to Pricewise (Daisycon affiliate) */}
            <SmartOverstapBanner analysisId={id} possibleSavings={analysis.possible_savings} />

            <ReportSummary analysis={analysis} input={input} />

            {/* Voorschot Check — prominent als er een afwijking is */}
            <VoorschotCheckCard voorschotCheck={analysis.voorschot_check} />

            <div className="mt-10 grid lg:grid-cols-3 gap-6">
              <OpvallendePunten punten={analysis.opvallende_punten} riskFactors={analysis.risk_factors} />
              <TipsCard tips={analysis.tips} />
            </div>

            {/* Hidden costs — verborgen kosten (alleen tonen als er items zijn) */}
            <HiddenCostsCard items={analysis.verborgen_kosten || []} />

            {/* Marketing trucs — alleen tonen als er items zijn */}
            <MarketingTrucs items={analysis.marketing_trucs || []} />

            <ContractDetails analysis={analysis} />

            {/* Provider recommendations */}
            <ProvidersSection providers={analysis.recommended_providers || []} />

            {/* ACM-certified comparison sites (affiliate redirect) */}
            <ComparisonSection analysisId={id} possibleSavings={analysis.possible_savings} />

            {/* Bundle upsell if docs not yet unlocked */}
            {!documents_unlocked && (
              <BundleUpsell analysisId={id} />
            )}

            {/* Documents (always rendered; component handles locked vs unlocked) */}
            <DocumentsSection
              analysisId={id}
              unlocked={documents_unlocked}
              onUnlockClick={() => {
                const el = document.getElementById("bundle-upsell");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            />

            {/* Lawyer upsell */}
            <UpsellSection analysisId={id} hasIssues={analysis.has_issues} />
          </>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center text-sm text-slate-500">
          EnergieKontract · Eerlijke analyse, geen tussenpersoon. Geen juridisch advies — gebruik in combinatie met je leverancier.
        </div>
      </footer>
    </div>
  );
}

function RiskBadge({ score }) {
  const cfg = {
    Laag: { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-200", dot: "bg-emerald-500", label: "Laag risico" },
    Gemiddeld: { bg: "bg-amber-100", text: "text-amber-800", ring: "ring-amber-200", dot: "bg-amber-500", label: "Gemiddeld risico" },
    Hoog: { bg: "bg-red-100", text: "text-red-800", ring: "ring-red-200", dot: "bg-red-500", label: "Hoog risico" },
  };
  const c = cfg[score] || cfg.Gemiddeld;
  return (
    <div
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full ring-1 ${c.ring} ${c.bg} ${c.text} font-semibold text-sm`}
      data-testid="risk-badge"
    >
      <span className={`h-2.5 w-2.5 rounded-full ${c.dot} animate-pulse`} />
      {c.label}
    </div>
  );
}

function VoorschotTeaser({ yearlyDifference, severity }) {
  const color = severity === "high" ? "red" : "amber";
  const bgClass = color === "red" ? "from-red-50 to-amber-50 border-red-200" : "from-amber-50 to-orange-50 border-amber-200";
  const textClass = color === "red" ? "text-red-700" : "text-amber-700";
  return (
    <section className="mt-6" data-testid="voorschot-teaser">
      <div className={`bg-gradient-to-br ${bgClass} border-2 rounded-2xl p-5 sm:p-6 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.1)]`}>
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-xl bg-white border-2 ${color === "red" ? "border-red-200" : "border-amber-200"} grid place-items-center flex-shrink-0`}>
            <Wallet className={`h-6 w-6 ${textClass}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${textClass} mb-1`}>
              ⚠ Naheffing-waarschuwing
            </div>
            <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 leading-tight">
              Je voorschot is veel te laag — er komt een dikke naheffing aan
            </h3>
            <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">
              Onze analyse toont dat je <strong>circa €{yearlyDifference} aan naheffing</strong> kunt verwachten aan het einde van dit contractjaar omdat je voorschot niet matcht met je werkelijke verbruik. <strong>Ontgrendel het rapport hieronder</strong> om precies te zien hoeveel én hoe je dit voorkomt.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function VoorschotCheckCard({ voorschotCheck }) {
  if (!voorschotCheck) return null;
  const paid = Number(voorschotCheck.monthly_paid) || 0;
  const actual = Number(voorschotCheck.estimated_monthly_actual) || 0;
  const diff = Number(voorschotCheck.yearly_difference) || 0;
  const under = voorschotCheck.is_under_paying;
  const over = voorschotCheck.is_over_paying;
  const severity = voorschotCheck.severity || "low";
  
  // If amounts unknown / nothing meaningful, skip the card
  if (!paid && !actual && !diff) return null;
  
  let theme;
  if (under && severity === "high") {
    theme = { ring: "border-red-300", accent: "bg-red-600 text-white", badge: "Hoog risico op naheffing", icon: "text-red-600", bg: "bg-red-50" };
  } else if (under) {
    theme = { ring: "border-amber-300", accent: "bg-amber-500 text-white", badge: "Mogelijke naheffing", icon: "text-amber-600", bg: "bg-amber-50" };
  } else if (over) {
    theme = { ring: "border-blue-200", accent: "bg-blue-600 text-white", badge: "Je betaalt te veel voorschot", icon: "text-blue-600", bg: "bg-blue-50" };
  } else {
    theme = { ring: "border-emerald-200", accent: "bg-emerald-600 text-white", badge: "Voorschot klopt", icon: "text-emerald-600", bg: "bg-emerald-50" };
  }
  
  return (
    <section className="mt-10" data-testid="voorschot-check-card">
      <div className={`bg-white border-2 ${theme.ring} rounded-2xl overflow-hidden shadow-[0_8px_30px_-12px_rgba(15,23,42,0.08)]`}>
        <div className={`${theme.accent} px-5 sm:px-7 py-2.5 flex items-center justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em]">{theme.badge}</span>
          </div>
          {diff > 0 && under && (
            <span className="text-[11px] font-semibold opacity-95">Je betaalt nu te weinig voorschot</span>
          )}
        </div>
        <div className="p-5 sm:p-7">
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-1">
            {under ? "Je voorschot is te laag — naheffing op komst" : over ? "Je betaalt te veel voorschot" : "Je voorschot klopt prima"}
          </h3>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed">
            {voorschotCheck.warning_message || "Op basis van je werkelijke verbruik en de tarieven in je contract."}
          </p>

          <div className="grid sm:grid-cols-3 gap-3">
            <div className={`${theme.bg} rounded-xl p-4 border border-slate-100`}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Je betaalt nu</div>
              <div className="font-heading text-2xl font-bold text-slate-900">€{Math.round(paid)}<span className="text-xs text-slate-500 font-normal">/mnd</span></div>
            </div>
            <div className={`${theme.bg} rounded-xl p-4 border border-slate-100`}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Eigenlijke kost</div>
              <div className={`font-heading text-2xl font-bold ${theme.icon}`}>€{Math.round(actual)}<span className="text-xs text-slate-500 font-normal">/mnd</span></div>
            </div>
            <div className={`${theme.bg} rounded-xl p-4 border border-slate-100`}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                {under ? "Verwachte naheffing" : over ? "Verwachte terugbetaling" : "Verschil per jaar"}
              </div>
              <div className={`font-heading text-2xl font-bold ${under ? "text-red-700" : over ? "text-blue-700" : "text-emerald-700"}`}>
                {under ? "+" : over ? "−" : ""}€{Math.abs(Math.round(diff))}<span className="text-xs text-slate-500 font-normal">/jaar</span>
              </div>
            </div>
          </div>

          {under && (
            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 leading-relaxed">
              <strong className="text-slate-900">💡 Wat dit voor jou betekent:</strong> Aan het einde van dit contractjaar krijg je een naheffing van ongeveer <strong>€{Math.abs(Math.round(diff))}</strong>. Door nu over te stappen naar een leverancier met een eerlijker tarief, voorkom je niet alleen die naheffing maar bespaar je ook elk jaar op je energiekosten.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function OpvallendePunten({ punten = [], riskFactors = [] }) {
  const items = (punten && punten.length) ? punten : riskFactors.map(r => ({
    title: r.title,
    description: r.description,
    type: r.severity === "high" ? "danger" : r.severity === "medium" ? "warning" : "info",
  }));
  const iconFor = (type) => {
    if (type === "danger") return <ShieldAlert className="h-5 w-5 text-red-600" />;
    if (type === "warning") return <AlertTriangle className="h-5 w-5 text-amber-600" />;
    return <Info className="h-5 w-5 text-blue-700" />;
  };
  const bgFor = (type) => {
    if (type === "danger") return "bg-red-50 border-red-100";
    if (type === "warning") return "bg-amber-50 border-amber-100";
    return "bg-blue-50 border-blue-100";
  };
  const fmtImpact = (n) => {
    if (n === undefined || n === null || n === 0) return null;
    const abs = Math.abs(Math.round(n));
    return { abs, isCost: n > 0 };
  };
  return (
    <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="opvallende-punten-card">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-xl font-semibold text-slate-900">Wat onze experts vonden</h3>
        <span className="text-xs text-slate-500">{items.length} bevindingen</span>
      </div>
      {items.length === 0 ? (
        <p className="text-slate-500">Geen specifieke risico's gevonden in je contract.</p>
      ) : (
        <div className="space-y-3">
          {items.map((p, i) => {
            const impact = fmtImpact(p.financial_impact);
            return (
              <div key={i} className={`border rounded-xl p-4 flex items-start gap-3 ${bgFor(p.type)}`} data-testid={`opvallend-item-${i}`}>
                <div className="mt-0.5">{iconFor(p.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="font-heading font-semibold text-slate-900 leading-snug">{p.title}</div>
                    {impact && (
                      <span
                        className={`flex-shrink-0 inline-flex items-center text-xs font-bold whitespace-nowrap px-2 py-1 rounded-full ${
                          impact.isCost ? "bg-red-600 text-white" : "bg-emerald-600 text-white"
                        }`}
                        data-testid={`opvallend-impact-${i}`}
                      >
                        {impact.isCost ? "−" : "+"}€{impact.abs}/jr
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-700 leading-relaxed">{p.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HiddenCostsCard({ items = [] }) {
  if (!items.length) return null;
  const total = items.reduce((s, x) => s + (Number(x.amount_per_year) || 0), 0);
  return (
    <section className="mt-10" data-testid="hidden-costs-section">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-red-700 mb-1">Verborgen kosten</div>
            <h3 className="font-heading text-xl font-semibold text-slate-900">Wat je leverancier niet groot adverteert</h3>
          </div>
          {total > 0 && (
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Totaal per jaar</div>
              <div className="font-heading text-2xl font-bold text-red-700">€{Math.round(total)}</div>
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it, i) => (
            <div key={i} className="bg-red-50 border border-red-100 rounded-xl p-4" data-testid={`hidden-cost-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="font-heading font-semibold text-slate-900 text-sm leading-tight">{it.title}</div>
                <span className="flex-shrink-0 inline-flex items-center text-xs font-bold text-red-700 bg-white border border-red-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                  €{Math.round(Number(it.amount_per_year) || 0)}/jr
                </span>
              </div>
              <div className="text-xs text-slate-700 leading-relaxed">{it.explanation}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MarketingTrucs({ items = [] }) {
  if (!items.length) return null;
  return (
    <section className="mt-10" data-testid="marketing-trucs-section">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-400">⚠ Marketingtrucs</span>
        </div>
        <h3 className="font-heading text-xl sm:text-2xl font-bold mb-1">Trucs die wij in jouw contract herkenden</h3>
        <p className="text-sm text-slate-300 mb-5 max-w-2xl">
          Energieleveranciers gebruiken vaak slimme constructies die op het eerste gezicht aantrekkelijk lijken. Hier zijn de trucs die we in jouw contract vonden.
        </p>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="bg-white/[0.06] border border-white/10 rounded-xl p-4 backdrop-blur-sm" data-testid={`marketing-truc-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="font-heading font-semibold text-white text-sm leading-tight">{it.trick}</div>
                {Number(it.impact_euros) > 0 && (
                  <span className="flex-shrink-0 inline-flex items-center text-xs font-bold bg-amber-400 text-slate-900 rounded-full px-2 py-0.5 whitespace-nowrap">
                    Kost je €{Math.round(Number(it.impact_euros))}/jr
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-200 leading-relaxed">{it.how_it_works}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TipsCard({ tips = [] }) {
  // Accept both string[] and {title, description}[]
  const items = tips.map((t) =>
    typeof t === "string" ? { title: t, description: "" } : { title: t.title || "", description: t.description || "" }
  );
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-2xl p-6 sm:p-8" data-testid="tips-card">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <h3 className="font-heading text-xl font-semibold text-slate-900">Aanbevolen acties</h3>
      </div>
      <ul className="space-y-3.5">
        {items.map((tip, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700" data-testid={`tip-${i}`}>
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
            <span className="leading-relaxed">
              {tip.title && <strong className="text-slate-900 block">{tip.title}</strong>}
              {tip.description && <span className="text-slate-600">{tip.description}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContractDetails({ analysis }) {
  const rows = [
    { label: "Contracttype", value: analysis.contract_type, icon: TrendingUp },
    { label: "Looptijd", value: analysis.contract_term, icon: Info },
    { label: "Opzegboete", value: analysis.cancellation_fee, icon: AlertTriangle },
    { label: "Leveringskosten", value: analysis.delivery_costs, icon: TrendingDown },
    { label: "Terugleververgoeding", value: analysis.feed_in_tariff || "N.v.t.", icon: Info },
    { label: "Dynamische tarieven", value: analysis.dynamic_tariffs ? "Ja" : "Nee", icon: Info },
    { label: "Automatische verlenging", value: analysis.automatic_renewal ? "Ja — let op opzegtermijn" : "Nee", icon: AlertTriangle },
  ];
  return (
    <section className="mt-10" data-testid="contract-details-section">
      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Contract details</h3>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between px-5 py-4" data-testid={`contract-row-${i}`}>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-blue-50 grid place-items-center">
                <r.icon className="h-4 w-4 text-blue-700" />
              </div>
              <span className="text-sm font-medium text-slate-700">{r.label}</span>
            </div>
            <span className="text-sm text-slate-900 font-heading font-semibold text-right max-w-[60%]">{r.value || "Onbekend"}</span>
          </div>
        ))}
      </div>
      {analysis.fine_print && analysis.fine_print.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="fine-print-card">
          <h4 className="font-heading font-semibold text-slate-900 mb-3">Belangrijke kleine letters</h4>
          <ul className="space-y-2.5">
            {analysis.fine_print.map((fp, i) => (
              <li key={i} className="text-sm text-slate-600 flex gap-2 items-start" data-testid={`fine-print-${i}`}>
                <span className="text-slate-400 mt-0.5">·</span>
                <span>{fp}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
